import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AppSplash } from "../components/ProtectedRoute";
import { homeRoute, lastRoute, forgetLastRoute, routeLabel } from "../lib/routeMemory";
import { ArrowRight, X } from "lucide-react";

export default function Login() {
  const { user, login, error } = useAuth();
  const [idleNotice] = useState(() => { try { const r = localStorage.getItem("pas_logout_reason"); localStorage.removeItem("pas_logout_reason"); return r; } catch { return null; } });
  const NOTICES = { idle: "45 dakika işlem yapılmadığı için oturumunuz güvenlik amacıyla kapatıldı. Lütfen yeniden giriş yapın.", closed: "Oturumunuz yönetici tarafından kapatıldı. Lütfen yeniden giriş yapın.", expired: "Oturum süreniz doldu. Lütfen yeniden giriş yapın." };
  const [last, setLast] = useState(() => lastRoute(null));   // son çalışılan ekran → "Kaldığın yerden devam et"
  const [email, setEmail] = useState(last?.user || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user === null) return <AppSplash />;
  if (user) return <Navigate to={homeRoute(user)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) navigate(homeRoute(ok));   // oturum düşmüşse kaldığı ekrana döner
  };
  const dismissLast = () => { forgetLastRoute(); setLast(null); };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-slate-900">
        <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-white px-3 py-2 shadow-md" data-testid="login-logo">
              <img src="/wilco-logo.png" alt="WILCO" className="h-14 w-auto" />
            </div>
            <div>
              <div className="text-lg font-semibold tracking-wide">WILCO_PAS</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-300">Demo v0.1</div>
            </div>
          </div>
          <img src="/login-bg.jpg" alt="Wilco Susuz Temizlik" data-testid="login-bg" className="w-full rounded-xl shadow-2xl ring-1 ring-white/10 my-6" />
          <div className="max-w-md">
            <h1 className="text-4xl font-bold leading-tight tracking-tight">
              Hizmet girişini tek noktadan yönetin.
            </h1>
          </div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center gap-5 p-6 sm:p-8 bg-slate-50 grain">
        <img src="/login-bg.jpg" alt="Wilco Susuz Temizlik" data-testid="login-bg-mobile" className="lg:hidden w-full max-w-sm rounded-xl shadow-sm border border-slate-200" />
        <form onSubmit={submit} className="w-full max-w-sm bg-white border border-slate-200 rounded-xl p-8 shadow-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-1" data-testid="login-logo-mobile">
            <img src="/wilco-logo.png" alt="WILCO" className="h-10 w-auto" />
            <span className="text-base font-semibold tracking-wide text-slate-900">WILCO_PAS</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">Giriş Yap</h2>
            <p className="text-sm text-slate-500 mt-1">Kullanıcı hesabınızla devam edin.</p>
          </div>
          {last && (
            <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2" data-testid="login-resume-badge">
              <ArrowRight className="w-4 h-4 text-amber-700 shrink-0" />
              <div className="min-w-0 text-xs">
                <div className="font-bold text-amber-900">Kaldığın yerden devam et</div>
                <div className="text-amber-800 truncate" data-testid="login-resume-label">{routeLabel(last.path)}{last.at ? ` · ${new Date(last.at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}` : ""}</div>
              </div>
              <button type="button" onClick={dismissLast} title="Genel Bakış'tan başla" data-testid="login-resume-dismiss" className="ml-auto p-1 rounded hover:bg-amber-100 text-amber-800"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">E-posta</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Şifre</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
            />
          </div>

          {NOTICES[idleNotice] && !error && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-3 py-2" data-testid="login-idle-notice" data-reason={idleNotice}>{NOTICES[idleNotice]}</div>}
          {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full py-2.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors disabled:opacity-60"
          >
            {loading ? "Giriş yapılıyor…" : "Giriş Yap"}
          </button>
        </form>
      </div>
    </div>
  );
}
